// Défis du groupe (D-047), enrichis par D-151 (ce qui manquait par rapport à Playbook) : la façon de saisir (un chiffre,
// des séries « X essais × N séries », un chrono, un compte à rebours), l'agrégat au choix (meilleur, moyenne, total,
// dernier), des paliers libres, la progression de chacune, les contributrices d'un défi collectif, des idées de défis.
// Le même calcul que la base (_challenge_agg, _challenge_ranks : palmarès, podium de la notification).
// Défis inter-catégories (D-095) : seuls les résultats validés par le coach de la catégorie comptent ; un podium par
// catégorie, et « Tous » entre catégories voisines en âge.
import { agree, displayName, type TeamGender } from './labels';

export type ChallengeMode = 'simple' | 'series' | 'chrono' | 'rebours' | 'presences' | 'mainsvives';
/** Défi Mains vives (D-165) : le temps de réaction moyen (le plus bas gagne) ou les cibles touchées (le plus haut gagne). */
export type MvMeasure = 'avg' | 'hits';
export const MV_MEASURE_LABEL: Record<MvMeasure, string> = { avg: 'Temps de réaction moyen', hits: 'Cibles touchées' };
/** Défi de présences (D-160) : la période comptée. */
export type CountPeriod = 'month' | 'quarter' | 'season';
export type Aggregate = 'best' | 'avg' | 'sum' | 'last';
export interface TierDef { name: string; threshold: number }
/** Le palier atteint : son nom, son niveau (1 = le premier) sur le nombre de paliers. */
export interface TierHit { name: string; level: number; of: number }
export type EntryStatus = 'attente' | 'valide' | 'refuse';
export interface ChallengeLike {
  kind: 'individual' | 'collective'; better: 'higher' | 'lower'; bronze: number | null; silver: number | null; gold: number | null; target: number | null;
  mode?: ChallengeMode; aggregate?: Aggregate; tiers?: TierDef[] | null; series_size?: number | null; unit?: string | null;
  mv_level?: number | null; mv_measure?: MvMeasure | null; mv_duration_s?: number | null;
}
/** Un résultat ; sans statut (défi d'une seule équipe), il compte. Dans l'ordre où ils ont été notés. */
export interface EntryLike { person_id: string; value: number; status?: EntryStatus; team_id?: string; created_at?: string }

export const MODE_LABEL: Record<ChallengeMode, string> = { simple: 'Un chiffre', series: 'Séries', chrono: 'Chrono', rebours: 'Compte à rebours', presences: 'Présences', mainsvives: 'Mains vives' };
export const PERIOD_LABEL: Record<CountPeriod, string> = { month: 'Le mois', quarter: 'Le trimestre', season: 'La saison' };
export const AGG_LABEL: Record<Aggregate, string> = { best: 'Meilleur', avg: 'Moyenne', sum: 'Total', last: 'Dernier' };
/** « Record » au chrono, « Moyenne », « Total »… : le mot du score d'une joueuse. */
export const aggWord = (c: ChallengeLike): string => (c.mode === 'presences' ? 'Présences'
  : (c.aggregate ?? 'best') === 'best' && c.better === 'lower' ? 'Record' : AGG_LABEL[c.aggregate ?? 'best']);

/** Ce qui compte au classement : les résultats validés (dans un défi d'équipe, tous le sont). */
export const counted = <E extends EntryLike>(entries: E[]): E[] => entries.filter(e => (e.status ?? 'valide') === 'valide');
const inOrder = <E extends EntryLike>(entries: E[]): E[] =>
  entries.some(e => e.created_at) ? [...entries].sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? '')) : entries;

/** L'agrégat d'une suite de résultats (dans l'ordre) : moyenne au centième, total, dernier, meilleur (le plus court au chrono). */
export function aggregateOf(agg: Aggregate | undefined, better: 'higher' | 'lower', values: number[]): number {
  if (!values.length) return 0;
  switch (agg ?? 'best') {
    case 'avg': return Math.round((values.reduce((a, v) => a + v, 0) / values.length) * 100) / 100;
    case 'sum': return values.reduce((a, v) => a + v, 0);
    case 'last': return values[values.length - 1]!;
    default: return better === 'lower' ? Math.min(...values) : Math.max(...values);
  }
}

/** Une série : les réussites ramenées à la taille de série (8 sur 20 pour une série de 10 : 4). */
export function seriesValue(made: number, attempts: number | null | undefined, size: number): number {
  const att = attempts && attempts > 0 ? attempts : size;
  return att === size ? made : Math.round((made * size / att) * 100) / 100;
}

/** Les paliers d'un défi, du premier au plus haut : ses paliers libres, sinon bronze / argent / or (défis d'avant D-151).
 *  Au chrono, le plus haut est le temps le plus court. */
export function tiersOf(c: ChallengeLike): TierDef[] {
  const list = c.tiers?.length ? c.tiers.filter(t => t && t.name && Number.isFinite(Number(t.threshold))).map(t => ({ name: t.name, threshold: Number(t.threshold) }))
    : ([['Bronze', c.bronze], ['Argent', c.silver], ['Or', c.gold]] as const).filter(([, v]) => v != null).map(([name, v]) => ({ name, threshold: Number(v) }));
  return list.sort((a, b) => (c.better === 'lower' ? b.threshold - a.threshold : a.threshold - b.threshold));
}

const reaches = (c: ChallengeLike, v: number, t: number) => (c.better === 'lower' ? v <= t : v >= t);

/** Le palier atteint par un score (au chrono, il faut être sous le seuil). */
export function tierOf(c: ChallengeLike, v: number): TierHit | null {
  const ts = tiersOf(c);
  let hit: TierHit | null = null;
  ts.forEach((t, i) => { if (reaches(c, v, t.threshold)) hit = { name: t.name, level: i + 1, of: ts.length }; });
  return hit;
}

/** Le prochain palier à atteindre (« prochain palier : Or à 18 »), sinon null. */
export function nextTier(c: ChallengeLike, v: number | null): TierDef | null {
  return tiersOf(c).find(t => v == null || !reaches(c, v, t.threshold)) ?? null;
}

/** La couleur d'un palier : le plus haut en or, puis argent, puis bronze ; les autres sans médaille. */
export function tierClass(hit: TierHit | null): 'or' | 'argent' | 'bronze' | 'plain' | null {
  if (!hit) return null;
  const fromTop = hit.of - hit.level;
  return fromTop === 0 ? 'or' : fromTop === 1 ? 'argent' : fromTop === 2 ? 'bronze' : 'plain';
}

/** Classement : le score de chacune (l'agrégat de ses résultats validés), du meilleur au moins bon. */
export function ranking(c: ChallengeLike, entries: EntryLike[]): { person_id: string; best: number; tries: number; tier: TierHit | null; rank: number }[] {
  const byPerson = new Map<string, number[]>();
  for (const e of inOrder(counted(entries))) byPerson.set(e.person_id, [...(byPerson.get(e.person_id) ?? []), Number(e.value)]);
  const rows = [...byPerson.entries()].map(([person_id, vs]) => {
    const best = aggregateOf(c.aggregate, c.better, vs);
    return { person_id, best, tries: vs.length, tier: tierOf(c, best) };
  }).sort((a, b) => (c.better === 'lower' ? a.best - b.best : b.best - a.best));
  // Ex aequo : même rang.
  return rows.map((r, i) => ({ ...r, rank: i > 0 && rows[i - 1]!.best === r.best ? rows.findIndex(x => x.best === r.best) + 1 : i + 1 }));
}

/** Défi collectif : total et progression vers l'objectif (résultats validés seulement). */
export function collective(c: ChallengeLike, entries: EntryLike[]): { total: number; pct: number; done: boolean } {
  const total = counted(entries).reduce((a, e) => a + Number(e.value), 0);
  const pct = c.target ? Math.min(100, Math.round((total / c.target) * 100)) : 0;
  return { total, pct, done: !!c.target && total >= c.target };
}

/** Défi collectif : les contributrices, de la plus grande part à la plus petite (leur total et leur part du total). */
export function contributors(entries: EntryLike[]): { person_id: string; total: number; pct: number }[] {
  const by = new Map<string, number>();
  for (const e of counted(entries)) by.set(e.person_id, (by.get(e.person_id) ?? 0) + Number(e.value));
  const all = [...by.values()].reduce((a, v) => a + v, 0);
  return [...by.entries()].map(([person_id, total]) => ({ person_id, total, pct: all > 0 ? Math.round((total / all) * 100) : 0 }))
    .sort((a, b) => b.total - a.total);
}

/** Ma progression : chacun de mes résultats, dans l'ordre, et le score après chacun (l'agrégat jusque-là). */
export function progression(c: ChallengeLike, entries: EntryLike[], personId: string): { value: number; score: number; at: string | null }[] {
  const mine = inOrder(counted(entries)).filter(e => e.person_id === personId);
  return mine.map((e, i) => ({ value: Number(e.value), score: aggregateOf(c.aggregate, c.better, mine.slice(0, i + 1).map(x => Number(x.value))), at: e.created_at ?? null }));
}

const frNum = (v: number, max = 2) => v.toLocaleString('fr-FR', { maximumFractionDigits: max });
/** Un temps : « 4,62 s », « 1:02,35 ». */
export function fmtChrono(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return '0 s';
  const cs = Math.round(sec * 100), m = Math.floor(cs / 6000), rest = (cs % 6000) / 100;
  return m > 0 ? `${m}:${rest.toFixed(2).padStart(5, '0').replace('.', ',')}` : `${frNum(rest)} s`;
}
/** Un score affiché : au chrono, un temps ; en séries, « 3,8 / 5 » (le total : un nombre) ; sinon le nombre et l'unité. */
export function fmtScore(c: ChallengeLike, v: number): string {
  if (c.mode === 'chrono') return fmtChrono(v);
  if (c.mode === 'presences') return `${frNum(v)} présence${v >= 2 ? 's' : ''}`;
  if (c.mode === 'mainsvives') return c.mv_measure === 'hits' ? `${frNum(v)} touchée${v >= 2 ? 's' : ''}` : `${frNum(v)} s`;
  if (c.mode === 'series' && c.series_size && (c.aggregate ?? 'best') !== 'sum') return `${frNum(v, 1)} / ${c.series_size}`;
  return `${frNum(v)}${c.unit ? ` ${c.unit}` : ''}`;
}

/** Comment on joue, en une ligne : « Séries de 5 · moyenne », « Chrono · record », « En 60 s · meilleur ». */
export function rulesLine(c: ChallengeLike & { series_count?: number | null; countdown_s?: number | null; count_from?: string | null; ends_on?: string | null }): string {
  const agg = aggWord(c).toLowerCase();
  if (c.kind === 'collective') return 'Objectif commun';
  if (c.mode === 'presences') return `Compté par l’app à chaque entraînement${c.count_from && c.ends_on ? ` · ${periodText(c.count_from, c.ends_on)}` : ''}`;
  if (c.mode === 'mainsvives') return `Niveau ${c.mv_level ?? 1} en ${c.mv_duration_s ?? 60} s · ${c.mv_measure === 'hits' ? 'touchées' : 'temps moyen'} · ${agg}`;
  if (c.mode === 'series') return `${c.series_count && c.series_count > 1 ? `${c.series_count} séries de ${c.series_size}` : `Séries de ${c.series_size}`} · ${agg}`;
  if (c.mode === 'chrono') return `Au chrono · ${agg}`;
  if (c.mode === 'rebours') return `En ${c.countdown_s ?? 60} s · ${agg}`;
  return c.better === 'lower' ? `Au chrono · ${agg}` : agg.charAt(0).toUpperCase() + agg.slice(1);
}

// -----------------------------------------------------------------------------
// Défi de présences (D-160) : compté par l'app depuis l'appel des entraînements
// -----------------------------------------------------------------------------
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
/** La période comptée, comme la base : le mois en cours, ou ce mois et les deux suivants (la saison : ses dates, null ici). */
export function presenceRange(period: CountPeriod, today: Date): { from: string; to: string } | null {
  if (period === 'season') return null;
  const from = new Date(today.getFullYear(), today.getMonth(), 1);
  const to = new Date(today.getFullYear(), today.getMonth() + (period === 'quarter' ? 3 : 1), 0);
  return { from: iso(from), to: iso(to) };
}
/** « du 1er au 30 nov. », « du 1er nov. au 31 janv. », « du 1er sept. 2026 au 30 juin 2027 ». */
export function periodText(from: string, to: string): string {
  const [y1, m1, d1] = from.split('-').map(Number) as [number, number, number];
  const [y2, m2, d2] = to.split('-').map(Number) as [number, number, number];
  const day = (d: number) => (d === 1 ? '1er' : String(d));
  const mon = (y: number, m: number) => new Date(y, m - 1, 1).toLocaleDateString('fr-FR', { month: 'short' });
  if (y1 === y2 && m1 === m2) return `du ${day(d1)} au ${day(d2)} ${mon(y2, m2)}`;
  if (y1 === y2) return `du ${day(d1)} ${mon(y1, m1)} au ${day(d2)} ${mon(y2, m2)}`;
  return `du ${day(d1)} ${mon(y1, m1)} ${y1} au ${day(d2)} ${mon(y2, m2)} ${y2}`;
}

// -----------------------------------------------------------------------------
// Idées de défis à lancer en un geste (celles de Playbook, et ses séries)
// -----------------------------------------------------------------------------
export type IdeaTheme = 'tir' | 'balle' | 'physique' | 'match' | 'ensemble';
export const IDEA_THEMES: { key: IdeaTheme; label: string }[] = [
  { key: 'tir', label: 'Tir' }, { key: 'balle', label: 'Balle en main' }, { key: 'physique', label: 'Physique' },
  { key: 'match', label: 'En match' }, { key: 'ensemble', label: 'Ensemble' },
];
export interface ChallengeIdea {
  id: string; theme: IdeaTheme; icon: string; title: string; description: string; kind: 'individual' | 'collective'; mode: ChallengeMode; unit: string | null;
  aggregate: Aggregate; better?: 'higher' | 'lower'; target?: number; series_size?: number; series_count?: number; countdown_s?: number; tiers?: TierDef[];
}
const tiers = (b: number, a: number, o: number): TierDef[] => [{ name: 'Bronze', threshold: b }, { name: 'Argent', threshold: a }, { name: 'Or', threshold: o }];
/** Les idées, accordées au genre de l'équipe, rangées par thème. */
export const challengeIdeas = (g: TeamGender): ChallengeIdea[] => [
  // Tir
  { id: 'lf-5x5', theme: 'tir', icon: '🎯', title: 'Lancers francs 5 × 5', description: '5 séries de 5 lancers francs après l’entraînement.', kind: 'individual', mode: 'series',
    unit: 'réussis', aggregate: 'avg', series_size: 5, series_count: 5, tiers: tiers(3, 3.8, 4.4) },
  { id: 'tirs-1min', theme: 'tir', icon: '⏱', title: 'Paniers en 1 minute', description: 'Le plus de paniers possible en 60 secondes, au rebond seul.', kind: 'individual', mode: 'rebours',
    unit: 'paniers', aggregate: 'best', countdown_s: 60, tiers: tiers(10, 15, 20) },
  { id: 'lf-affilee', theme: 'tir', icon: '🔥', title: 'Lancers francs d’affilée', description: 'La plus longue série de lancers francs réussis sans en rater un.', kind: 'individual',
    mode: 'simple', unit: 'd’affilée', aggregate: 'best', better: 'higher' },
  { id: '3pts-mois', theme: 'tir', icon: '🏀', title: 'Tirs à 3 points du mois', description: 'Chaque 3 points réussi à l’entraînement s’ajoute.', kind: 'individual', mode: 'simple',
    unit: '3 pts', aggregate: 'sum', better: 'higher' },
  { id: 'lf-100', theme: 'tir', icon: '💯', title: '100 lancers francs', description: '100 lancers francs dans la semaine : combien de réussis ?', kind: 'individual', mode: 'simple',
    unit: 'réussis sur 100', aggregate: 'best', better: 'higher', tiers: tiers(60, 70, 80) },
  { id: 'tour-monde', theme: 'tir', icon: '🌍', title: 'Tour du monde', description: '10 tirs à chacune des 5 positions autour de la raquette ; on note les réussis de chaque position.',
    kind: 'individual', mode: 'series', unit: 'réussis', aggregate: 'avg', series_size: 10, series_count: 5, tiers: tiers(4, 5.5, 7) },
  { id: '3pts-1min', theme: 'tir', icon: '🎇', title: '3 points en 1 minute', description: 'Le plus de 3 points réussis en 60 secondes, avec un passeur.', kind: 'individual', mode: 'rebours',
    unit: '3 pts', aggregate: 'best', countdown_s: 60, tiers: tiers(5, 8, 11) },
  { id: 'lf-fatigue', theme: 'tir', icon: '🫁', title: 'Lancers francs sous fatigue', description: 'Un aller-retour en sprint, puis 2 lancers francs ; 10 fois de suite.', kind: 'individual', mode: 'series',
    unit: 'réussis', aggregate: 'sum', series_size: 2, series_count: 10, tiers: tiers(10, 14, 17) },
  // Balle en main
  { id: 'double-pas-1min', theme: 'balle', icon: '🔁', title: 'Double-pas en 1 minute', description: 'Le plus de double-pas réussis en 60 secondes, un côté puis l’autre.', kind: 'individual', mode: 'rebours',
    unit: 'paniers', aggregate: 'best', countdown_s: 60, tiers: tiers(12, 16, 20) },
  { id: 'main-faible', theme: 'balle', icon: '✋', title: 'Main faible en 1 minute', description: 'Double-pas de la main faible seulement : le plus de paniers en 60 secondes.', kind: 'individual', mode: 'rebours',
    unit: 'paniers', aggregate: 'best', countdown_s: 60, tiers: tiers(8, 12, 15) },
  { id: 'slalom', theme: 'balle', icon: '🐍', title: 'Slalom en dribble', description: 'Aller-retour entre les plots, main faible au retour, au chrono.', kind: 'individual', mode: 'chrono', unit: 's', aggregate: 'best' },
  { id: 'passes-mur', theme: 'balle', icon: '🧱', title: 'Passes au mur en 30 secondes', description: 'Passes à deux mains contre le mur, à 3 mètres : combien en 30 secondes ?', kind: 'individual', mode: 'rebours',
    unit: 'passes', aggregate: 'best', countdown_s: 30, tiers: tiers(25, 32, 40) },
  { id: 'deux-ballons', theme: 'balle', icon: '🤹', title: 'Dribble à deux ballons', description: 'Tenir le plus longtemps possible sans perdre un ballon.', kind: 'individual', mode: 'simple',
    unit: 's', aggregate: 'best', better: 'higher' },
  // Physique
  { id: 'sprint', theme: 'physique', icon: '⚡', title: 'Sprint aller-retour', description: 'Ligne de fond, ligne de fond, au chrono.', kind: 'individual', mode: 'chrono', unit: 's', aggregate: 'best' },
  { id: 'navettes', theme: 'physique', icon: '🏃', title: 'Navettes (suicides)', description: 'Lancers francs, milieu, lancers francs opposés, ligne de fond : aller et retour à chaque ligne, au chrono.',
    kind: 'individual', mode: 'chrono', unit: 's', aggregate: 'best' },
  { id: 'gainage', theme: 'physique', icon: '🧘', title: 'Gainage', description: 'La planche la plus longue, sur les avant-bras.', kind: 'individual', mode: 'simple', unit: 's', aggregate: 'best', better: 'higher',
    tiers: tiers(60, 90, 120) },
  { id: 'corde', theme: 'physique', icon: '🪢', title: 'Corde à sauter en 1 minute', description: 'Le plus de sauts en 60 secondes.', kind: 'individual', mode: 'rebours', unit: 'sauts', aggregate: 'best', countdown_s: 60,
    tiers: tiers(80, 110, 140) },
  { id: 'detente', theme: 'physique', icon: '🦘', title: 'Détente verticale', description: `Le saut le plus haut, mesuré au mur : ${agree(g, 'chacun', 'chacune')} part de sa meilleure marque.`, kind: 'individual', mode: 'simple',
    unit: 'cm', aggregate: 'best', better: 'higher' },
  // En match
  { id: 'rebonds-mois', theme: 'match', icon: '🙌', title: 'Rebonds du mois', description: 'Les rebonds pris en match s’ajoutent, d’après les stats.', kind: 'individual', mode: 'simple', unit: 'rebonds', aggregate: 'sum', better: 'higher' },
  { id: 'passes-d-mois', theme: 'match', icon: '🎁', title: 'Passes décisives du mois', description: 'Les passes décisives en match s’ajoutent, d’après les stats.', kind: 'individual', mode: 'simple', unit: 'passes', aggregate: 'sum', better: 'higher' },
  { id: 'interceptions', theme: 'match', icon: '🦅', title: 'Interceptions du mois', description: 'Les interceptions en match s’ajoutent, d’après les stats.', kind: 'individual', mode: 'simple', unit: 'interceptions', aggregate: 'sum', better: 'higher' },
  // Ensemble
  { id: '100-3pts', theme: 'ensemble', icon: '🤝', title: '100 paniers à 3 points', description: 'Toute l’équipe ensemble jusqu’à 100 tirs à 3 points réussis.', kind: 'collective', mode: 'simple',
    unit: 'paniers', aggregate: 'sum', target: 100 },
  { id: '1000-lf', theme: 'ensemble', icon: '🎯', title: '1 000 lancers francs réussis', description: `Tous les lancers francs réussis de la semaine, ${agree(g, 'chacun', 'chacune')} ajoute les siens.`, kind: 'collective', mode: 'simple',
    unit: 'réussis', aggregate: 'sum', target: 1000 },
  { id: '500-pompes', theme: 'ensemble', icon: '💪', title: '500 pompes en équipe', description: `Le total de la semaine, ${agree(g, 'chacun', 'chacune')} ajoute les siennes.`, kind: 'collective', mode: 'simple',
    unit: 'pompes', aggregate: 'sum', target: 500 },
  { id: '50-km', theme: 'ensemble', icon: '🏃', title: '50 km ensemble', description: `Les footings de ${agree(g, 'chacun', 'chacune')}, cumulés.`, kind: 'collective', mode: 'simple', unit: 'km', aggregate: 'sum', target: 50 },
  { id: '10000-dribbles', theme: 'ensemble', icon: '🏀', title: '10 000 dribbles', description: `Les dribbles comptés à la maison, ${agree(g, 'chacun', 'chacune')} ajoute les siens.`, kind: 'collective', mode: 'simple',
    unit: 'dribbles', aggregate: 'sum', target: 10000 },
];

// -----------------------------------------------------------------------------
// Défis inter-catégories (D-095)
// -----------------------------------------------------------------------------

/** Un podium par catégorie : le classement de chaque équipe, ses seuls résultats validés. */
export function rankingByTeam(c: ChallengeLike, entries: EntryLike[]): Map<string, ReturnType<typeof ranking>> {
  const byTeam = new Map<string, EntryLike[]>();
  for (const e of counted(entries)) if (e.team_id) byTeam.set(e.team_id, [...(byTeam.get(e.team_id) ?? []), e]);
  return new Map([...byTeam.entries()].map(([t, es]) => [t, ranking(c, es)]));
}

/** Échelon d'âge d'une catégorie : U7 et moins, U9, U11, U13, U15, U17/U18, U20/U21, seniors (Seniors, SE,
 *  Loisir, Vétérans). Inconnue : null. */
export function ageRung(category: string | null | undefined): number | null {
  const c = (category ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toUpperCase();
  if (!c) return null;
  if (/^(SE|SENIORS?|LOISIRS?|VETERANS?|ADULTES?)\b/.test(c)) return 7;
  const m = /^U\s?(\d{1,2})\b/.exec(c);
  if (!m) return null;
  const n = Number(m[1]);
  return n <= 7 ? 0 : n <= 9 ? 1 : n <= 11 ? 2 : n <= 13 ? 3 : n <= 15 ? 4 : n <= 18 ? 5 : n <= 21 ? 6 : 7;
}

/** « Tous » (un seul classement) : seulement entre catégories voisines en âge — U13 et U15, U15 et U18,
 *  U20 et seniors, seniors entre eux. Une catégorie inconnue, ou une seule catégorie : par catégorie seulement. */
export function canRankTogether(categories: (string | null | undefined)[]): boolean {
  if (categories.length < 2) return false;
  const rungs = categories.map(ageRung);
  if (rungs.some(r => r == null)) return false;
  const n = rungs as number[];
  return Math.max(...n) - Math.min(...n) <= 1;
}

export interface TeamLike { id: string; name: string; short_name?: string | null; category: string | null; gender: string }

/** Libellé court d'une catégorie : son sigle s'il en a un, sinon « U13 F », « U15 G », « SF », « SG ». */
export function teamShort(t: TeamLike): string {
  if (t.short_name?.trim()) return t.short_name.trim();
  const g = t.gender === 'F' ? 'F' : t.gender === 'M' ? 'G' : '';
  const cat = (t.category ?? '').trim();
  if (ageRung(cat) === 7 && /^(SE|S[EÉ]NIOR)/i.test(cat)) return g ? `S${g}` : 'Seniors';
  if (/^U\s?\d{1,2}$/i.test(cat)) return `${cat.replace(/\s+/g, '').toUpperCase()}${g ? ` ${g}` : ''}`;
  return t.name;
}

/** Libellés courts de plusieurs catégories, sans doublon (deux « SF » : leurs noms). */
export function shortLabels(teams: TeamLike[]): Map<string, string> {
  const short = teams.map(t => [t.id, teamShort(t)] as const);
  const n = new Map<string, number>();
  for (const [, s] of short) n.set(s, (n.get(s) ?? 0) + 1);
  return new Map(short.map(([id, s]) => [id, (n.get(s) ?? 0) > 1 ? teams.find(t => t.id === id)!.name : s]));
}

/** Catégories à cocher : l'équipe qui lance d'abord, puis celles du même genre, des plus âgées aux plus jeunes. */
export function sortForPick<T extends TeamLike>(teams: T[], hostId: string): T[] {
  const host = teams.find(t => t.id === hostId);
  const key = (t: T) => [t.id === hostId ? 0 : 1, host && t.gender === host.gender ? 0 : 1, -(ageRung(t.category) ?? -1)];
  return [...teams].sort((a, b) => {
    const ka = key(a), kb = key(b);
    for (let i = 0; i < ka.length; i++) if (ka[i] !== kb[i]) return ka[i]! - kb[i]!;
    return a.name.localeCompare(b.name, 'fr');
  });
}

export interface RankPerson { person_id: string; first_name: string; nickname?: string | null; last_initial?: string | null }

/** Noms d'un classement entre catégories : le surnom ou le prénom ; deux homonymes : l'initiale du nom en plus
 *  (« Emma D. »). Les mineures restent au prénom, comme partout dans l'app. */
export function rankNames(people: RankPerson[]): Map<string, string> {
  const uniq = [...new Map(people.map(p => [p.person_id, p])).values()];
  const n = new Map<string, number>();
  for (const p of uniq) { const k = displayName(p).toLowerCase(); n.set(k, (n.get(k) ?? 0) + 1); }
  return new Map(uniq.map(p => {
    const s = displayName(p), ini = p.last_initial?.trim();
    return [p.person_id, (n.get(s.toLowerCase()) ?? 0) > 1 && ini ? `${s} ${ini.charAt(0).toUpperCase()}.` : s];
  }));
}

/** Pourcentage arrondi, borné à 100 (participation, part d'une catégorie). */
export const pctOf = (n: number, of: number): number => (of > 0 ? Math.min(100, Math.round((n / of) * 100)) : 0);

/** Défi collectif inter-catégories : la part de chaque catégorie dans le total du club. */
export function teamShares(rows: { team_id: string; total: number }[]): { team_id: string; total: number; pct: number }[] {
  const all = rows.reduce((a, r) => a + Number(r.total), 0);
  return rows.map(r => ({ team_id: r.team_id, total: Number(r.total), pct: pctOf(Number(r.total), all) }));
}

/** « U15 F et U13 F », « U15 F, U13 F et U11 F ». */
export function joinFr(items: string[]): string {
  return items.length < 2 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')} et ${items[items.length - 1]}`;
}
